import React, { memo, useMemo, useState } from 'react';

import { Mask } from 'v2/components/Shared/ValuesVisibility';

import './EliteProgressBar.css';

interface IProps {
  producerGains: number;
}

interface ILevel {
  name: string;
  label: string;
  color: string;
  valueToAchieve: number;
  valueToAdd: number;
}

const EliteProgressBar = memo(({ producerGains }: IProps) => {
  const gains = producerGains > 0 ? producerGains : 0;

  const isGoldenBeltEligible = useMemo(() => producerGains > 20000000, [producerGains]);

  const maxAmountElite = 100000000;

  const [levels] = useState<ILevel[]>([
    { name: 'White Belt', label: 'R$ 0.00', color: '', valueToAchieve: 0, valueToAdd: 10000 },
    { name: 'Red Belt', label: 'R$ 100.000', color: '--red', valueToAchieve: 100000, valueToAdd: 15000 },
    { name: 'Orange Belt', label: 'R$ 250.000', color: '--orange', valueToAchieve: 250000, valueToAdd: 25000 },
    { name: 'Green Belt', label: 'R$ 500.000', color: '--green', valueToAchieve: 500000, valueToAdd: 50000 },
    { name: 'Black Belt', label: 'R$ 1.000.000', color: '--black', valueToAchieve: 1000000, valueToAdd: 100000 },
    { name: '2º DAN', label: 'R$ 2.000.000', color: '', valueToAchieve: 2000000, valueToAdd: 100000 },
    { name: '3º DAN', label: 'R$ 3.000.000', color: '', valueToAchieve: 3000000, valueToAdd: 100000 },
    { name: '4º DAN', label: 'R$ 4.000.000', color: '', valueToAchieve: 4000000, valueToAdd: 100000 },
    { name: '5º DAN', label: 'R$ 5.000.000', color: '', valueToAchieve: 5000000, valueToAdd: 100000 },
    { name: '6º DAN', label: 'R$ 6.000.000', color: '', valueToAchieve: 6000000, valueToAdd: 100000 },
    { name: '7º DAN', label: 'R$ 7.000.000', color: '', valueToAchieve: 7000000, valueToAdd: 100000 },
    { name: '8º DAN', label: 'R$ 8.000.000', color: '', valueToAchieve: 8000000, valueToAdd: 100000 },
    { name: '9º DAN', label: 'R$ 9.000.000', color: '', valueToAchieve: 9000000, valueToAdd: 100000 },
    { name: 'Golden', label: 'R$ 10.000.000', color: '--golden', valueToAchieve: 10000000, valueToAdd: 1000000 },
    { name: '2º DAN', label: 'R$ 20.000.000', color: '', valueToAchieve: 20000000, valueToAdd: 1000000 },
    { name: '3º DAN', label: 'R$ 30.000.000', color: '', valueToAchieve: 30000000, valueToAdd: 1000000 },
    { name: '4º DAN', label: 'R$ 40.000.000', color: '', valueToAchieve: 40000000, valueToAdd: 1000000 },
    { name: '5º DAN', label: 'R$ 50.000.000', color: '', valueToAchieve: 50000000, valueToAdd: 1000000 },
    { name: '6º DAN', label: 'R$ 60.000.000', color: '', valueToAchieve: 60000000, valueToAdd: 1000000 },
    { name: '7º DAN', label: 'R$ 70.000.000', color: '', valueToAchieve: 70000000, valueToAdd: 1000000 },
    { name: '8º DAN', label: 'R$ 80.000.000', color: '', valueToAchieve: 80000000, valueToAdd: 1000000 },
    { name: '9º DAN', label: 'R$ 90.000.000', color: '', valueToAchieve: 90000000, valueToAdd: 1000000 },
    { name: 'Sensei', label: 'R$ 100.000.000', color: '', valueToAchieve: maxAmountElite, valueToAdd: 0 }
  ]);

  const getLevels = useMemo<ILevel[]>(() => {
    if (gains >= maxAmountElite) {
      return [levels[levels.length - 2], levels[levels.length - 1]];
    }

    const secondLevel = levels.findIndex(item => gains < item.valueToAchieve);

    if (!secondLevel) {
      return [];
    }

    const firstLevel = secondLevel - 1;

    return [levels[firstLevel], levels[secondLevel]];
  }, [gains, levels]);

  const generateLevelsIntermediare = useMemo(() => {
    const multipleBy = [1, 2, 3, 4, 5, 6, 7, 8, 9];

    if (gains >= maxAmountElite) {
      return [];
    }

    const [level] = getLevels;

    if (!level) {
      return [];
    }

    return multipleBy.map(multiplier => ({ valueToAchieve: level.valueToAchieve + level.valueToAdd * multiplier }));
  }, [getLevels, gains]);

  const getLevelsIntermediare = useMemo<Partial<ILevel>[]>(() => {
    if (getLevels.length > 0) {
      return generateLevelsIntermediare.filter(
        item => item.valueToAchieve >= getLevels[0].valueToAchieve && item.valueToAchieve <= getLevels[1].valueToAchieve
      );
    }

    return [];
  }, [getLevels, generateLevelsIntermediare]);

  const allLevels = useMemo(() => {
    return getLevels.length > 0 ? [getLevels[0], ...getLevelsIntermediare, getLevels[1]] : [];
  }, [getLevels, getLevelsIntermediare]);

  const activeColor = useMemo(() => {
    if (!getLevels.length) {
      return '';
    }

    if (gains >= maxAmountElite) {
      return getLevels[1].color;
    }

    return getLevels[0].color;
  }, [getLevels, gains]);

  const activeLevelValue = useMemo(() => {
    if (!allLevels.length) {
      return 0;
    }

    if (gains >= maxAmountElite) {
      return getLevels[1].valueToAchieve;
    }

    return allLevels.find(item => gains <= item.valueToAchieve).valueToAchieve;
  }, [allLevels, getLevels, gains]);

  return (
    <div className='w-full'>
      <div
        className={`relative flex w-full justify-between ${gains >= 2000000 ? 'elite-slide-premium' : 'elite-slide'} ${isGoldenBeltEligible ? '--golden' : activeColor || ''} `}
      >
        {allLevels.map(item => (
          <div
            key={item.valueToAchieve}
            className={`elite-slide-item relative text-center ${
              activeLevelValue === item.valueToAchieve ? '--active' : ''
            } first:text-left last:text-right`}
          >
            {item.name && <label className='mb-6 block text-sm font-medium text-[#586375]'>{item.name}</label>}
            {item.label && (
              <span className='text-[15px] text-[#586375]'>
                <Mask>{item.label}</Mask>
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

export default EliteProgressBar;
